import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Receipt, Car, Check, Clock } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import LotteryCodeInput from "@/components/LotteryCodeInput";
import PrecontoButton from "@/components/pos/PrecontoButton";
import { authorizeCardIfNeeded } from "@/lib/paymentPos";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";

// Servizi predefiniti (in produzione: caricati dai prodotti del tenant)
const DEFAULT_SERVICES = [
  { id: "esterno", label: "Lavaggio esterno", price: 8, duration: 15, icon: "🚿" },
  { id: "interno", label: "Pulizia interno", price: 12, duration: 20, icon: "🧹" },
  { id: "completo", label: "Lavaggio completo", price: 18, duration: 30, icon: "🚗" },
  { id: "premium", label: "Premium + Cera", price: 28, duration: 45, icon: "✨" },
  { id: "tappetini", label: "Tappetini", price: 4, duration: 5, icon: "🟫" },
  { id: "polish", label: "Polish carrozzeria", price: 35, duration: 60, icon: "🪞" },
  { id: "motore", label: "Lavaggio motore", price: 15, duration: 20, icon: "🔧" },
  { id: "tergicristalli", label: "Liquido tergi", price: 3, duration: 2, icon: "💧" },
];

export default function POSAutolavaggio() {
  const [tenant, setTenant] = useState(null);
  const [plate, setPlate] = useState("");
  const [selected, setSelected] = useState([]); // array of services
  const [showPay, setShowPay] = useState(false);
  const [last, setLast] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [lotteryCode, setLotteryCode] = useState("");
  const [services, setServices] = useState(DEFAULT_SERVICES);

  useEffect(() => {
    api.get("/tenant/me").then(r => setTenant(r.data));
    // Se il tenant ha prodotti, usali; altrimenti default
    api.get("/products?mode=autolavaggio").then(r => {
      if (r.data && r.data.length > 0) {
        const fromProds = (Array.isArray(r.data) ? r.data : []).filter(p => p.visible !== false).map(p => ({
          id: p.id,
          label: p.name,
          price: p.price,
          duration: p.duration_min || 15,
          icon: "🚗",
          vat: p.vat,
        }));
        if (fromProds.length >= 3) setServices(fromProds);
      }
    });
  }, []);

  const total = selected.reduce((s, l) => s + l.price * l.qty, 0);
  const totalDuration = selected.reduce((s, l) => s + l.duration * l.qty, 0);

  const toggleService = (svc) => {
    const i = selected.findIndex(s => s.id === svc.id);
    if (i >= 0) {
      setSelected((Array.isArray(selected) ? selected : []).filter(s => s.id !== svc.id));
    } else {
      setSelected([...selected, { ...svc, qty: 1 }]);
    }
  };

  const remove = (i) => {
    const u = [...selected]; u.splice(i, 1); setSelected(u);
  };

  const [precontoOrderId, setPrecontoOrderId] = useState(null);

  const close = async (method) => {
    if (!selected.length) return;
    const pos = await authorizeCardIfNeeded({ method, amount: total });
    if (!pos.ok) { toast.error(pos.message || "Pagamento carta non autorizzato"); return; }
    if (pos.mode === "pos") toast.success("Carta autorizzata sul POS");
    const offlineId = newOfflineId();
    let oid = precontoOrderId || null;
    const lines = (Array.isArray(selected) ? selected : []).map(s => ({
      name: s.label,
      qty: s.qty,
      price: s.price,
      vat: s.vat || 22,
    }));
    try {
      const cleanPlate = plate.trim().toUpperCase();
      const notes = cleanPlate ? `Targa: ${cleanPlate} · Durata stimata: ${totalDuration} min` : `Durata stimata: ${totalDuration} min`;
      if (precontoOrderId) {
        const upd = { lines, notes };
        if (lotteryCode) upd.lottery_code = lotteryCode;
        await api.patch(`/orders/${precontoOrderId}`, upd, SERVER_ATTEMPT);
        oid = precontoOrderId;
      } else {
        const r = await api.post("/orders", { mode: "autolavaggio", lines, payment_method: method, notes, lottery_code: lotteryCode || undefined, offline_id: offlineId }, SERVER_ATTEMPT);
        oid = r.data.id;
      }
      const c = await api.post(`/orders/${oid}/close-fiscal`, { payment_method: method }, SERVER_ATTEMPT);
      setLast(c.data); setShowPay(false); setShowScontrino(true);
      setSelected([]); setPlate(""); setLotteryCode(""); setPrecontoOrderId(null);
      toast.success(`Scontrino #${c.data.receipt_number}`);
    } catch (e) {
      const off = await recoverUnreachableSale(e, {
        tenant, offline_id: offlineId, server_order_id: oid, mode: "autolavaggio", lines,
        payment_method: method, lottery_code: lotteryCode || null,
      });
      if (off.printed) {
        setLast(off.order); setShowPay(false); setShowScontrino(true);
        setSelected([]); setPlate(""); setLotteryCode(""); setPrecontoOrderId(null);
        return;
      }
      if (!off.handled) toast.error("Errore");
    }
  };

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" data-testid="auto-back" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <Car className="w-5 h-5 text-[#0EA5E9]" />
        <div className="font-heading font-semibold tracking-tight">Velta One · Autolavaggio</div>
      </header>

      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        <section className="col-span-8 flex flex-col overflow-hidden">
          {/* Targa veicolo */}
          <div className="border-b border-velta-border bg-white p-4">
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Targa veicolo (opzionale)</label>
            <div className="flex items-center gap-3 max-w-md">
              <div className="flex-1 bg-yellow-400 border-2 border-velta-ink rounded-md flex items-center px-4 h-14">
                <span className="font-mono font-bold text-sm text-velta-ink mr-2">I</span>
                <input
                  data-testid="auto-plate"
                  value={plate}
                  onChange={e => setPlate(e.target.value.toUpperCase())}
                  placeholder="AB123CD"
                  maxLength="8"
                  className="flex-1 bg-transparent font-mono font-bold text-2xl tracking-widest text-velta-ink outline-none placeholder:text-velta-ink/40"
                />
              </div>
              {plate && <button data-testid="auto-plate-clear" onClick={() => setPlate("")} className="text-velta-mute"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>

          {/* Servizi */}
          <div className="flex-1 overflow-auto pos-scroll p-4">
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Scegli servizi</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {(Array.isArray(services) ? services : []).map(svc => {
                const active = selected.find(s => s.id === svc.id);
                return (
                  <button
                    key={svc.id}
                    data-testid={`auto-svc-${svc.id}`}
                    onClick={() => toggleService(svc)}
                    className={`relative bg-white border-2 rounded-md p-4 text-left btn-tactile transition ${active ? "border-[#0EA5E9] bg-[#0EA5E9]/5" : "border-velta-border hover:border-velta-ink"}`}
                  >
                    {active && <div className="absolute top-2 right-2 w-6 h-6 bg-[#0EA5E9] text-white rounded-full flex items-center justify-center"><Check className="w-4 h-4" /></div>}
                    <div className="text-3xl mb-2">{svc.icon}</div>
                    <div className="font-medium text-sm leading-tight mb-1">{svc.label}</div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-lg">{formatEUR(svc.price)}</span>
                      <span className="text-xs text-velta-mute"><Clock className="w-3 h-3 inline" /> {svc.duration}min</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <aside className="col-span-4 border-l border-velta-border bg-white flex flex-col overflow-hidden">
          <div className="p-4 border-b border-velta-border">
            <h2 className="font-heading font-semibold text-lg">Riepilogo</h2>
            {plate && <div className="text-xs text-velta-mute mt-1 font-mono">Targa: <span className="font-bold text-velta-ink">{plate}</span></div>}
          </div>
          <div className="flex-1 overflow-auto pos-scroll p-3">
            {selected.length === 0 ? <div className="text-center text-sm text-velta-mute p-8">Seleziona i servizi.</div> : (
              <div className="space-y-2">
                {(Array.isArray(selected) ? selected : []).map((l, i) => (
                  <div key={i} className="border border-velta-border rounded-md p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{l.icon}</span>
                      <div>
                        <div className="font-medium text-sm">{l.label}</div>
                        <div className="text-xs text-velta-mute"><Clock className="w-3 h-3 inline" /> {l.duration}min</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold">{formatEUR(l.price)}</span>
                      <button onClick={() => remove(i)} className="text-velta-error"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-velta-border p-4 bg-velta-bg">
            {totalDuration > 0 && (
              <div className="flex justify-between text-xs text-velta-mute mb-1">
                <span>Tempo stimato</span>
                <span data-testid="auto-duration" className="font-mono font-bold">{totalDuration} min</span>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale</span>
              <span data-testid="auto-total" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(total)}</span>
            </div>
            <PrecontoButton
              mode="autolavaggio"
              lines={(Array.isArray(selected) ? selected : []).map(s => ({ name: s.label, qty: s.qty, price: s.price, vat: s.vat || 22 }))}
              tenant={tenant}
              orderId={precontoOrderId}
              onOrderSaved={setPrecontoOrderId}
              testid="auto-preconto-btn"
            />
            <button data-testid="auto-pay-btn" disabled={!selected.length} onClick={() => setShowPay(true)} className="w-full h-14 bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-md font-medium text-lg flex items-center justify-center gap-2 btn-tactile disabled:opacity-40">
              <Receipt className="w-5 h-5" /> Scontrino
            </button>
          </div>
        </aside>
      </div>

      {showPay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowPay(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-semibold mb-4">Pagamento</h3>
            <div className="bg-velta-bg rounded-md p-4 mb-4 text-center"><div className="font-mono font-bold text-3xl">{formatEUR(total)}</div></div>
            <div className="mb-4"><LotteryCodeInput value={lotteryCode} onChange={setLotteryCode} testid="auto-lottery" /></div>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="auto-pay-cash" onClick={() => close("cash")} className="h-14 bg-velta-ink text-white rounded-md font-medium btn-tactile">Contanti</button>
              <button data-testid="auto-pay-card" onClick={() => close("card")} className="h-14 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Carta</button>
              <button data-testid="auto-pay-ticket" onClick={() => close("ticket")} className="h-14 bg-velta-bg border border-velta-border text-velta-ink rounded-md font-medium btn-tactile">Ticket</button>
            </div>
          </div>
        </div>
      )}
      {showScontrino && <ScontrinoPreview order={last} tenant={tenant} fiscal onClose={() => setShowScontrino(false)} />}
    </div>
  );
}
